<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

<mat-card class="block" *ngIf="showFirstSgaFreeRemediationNote" style="margin-bottom:1rem">
  <h2>Participation benefit</h2>
  <p class="meta" style="margin-bottom:0">
    First SGA evaluation challenges always include free remediation for scored participants with skill gaps.
    This acquisition benefit cannot be disabled.
  </p>
</mat-card>

<mat-card class="block" *ngIf="showParticipationPrize" style="margin-bottom:1rem">
  <h2>Participation benefit</h2>
  <p class="meta" style="margin-bottom:0.75rem">
    SahkaarX participation prize for open challenges: all scored participants with identified challenge skill gaps
    receive a free remediation learning plan. This is separate from winner prize tiers below.
  </p>
  <div class="actions" style="align-items:center;gap:1rem">
    <mat-slide-toggle [(ngModel)]="participationEnabled" [ngModelOptions]="{standalone: true}" [disabled]="busy">
      Enable free remediation for scored participants with gaps
    </mat-slide-toggle>
    <button mat-flat-button color="primary" type="button" (click)="saveParticipationPrize()" [disabled]="busy">
      Save participation prize
    </button>
  </div>
</mat-card>

<div class="grid-two">
  <mat-card class="block">
    <h2>{{ editingId ? 'Edit winner prize' : 'Add winner prize tier' }}</h2>
    <form class="form-grid" [formGroup]="form" (ngSubmit)="save()">
      <mat-form-field appearance="outline">
        <mat-label>Name</mat-label>
        <input matInput formControlName="name" placeholder="1st place" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Prize value / in-kind</mat-label>
        <input matInput formControlName="prizeValue" />
      </mat-form-field>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Description</mat-label>
        <textarea matInput rows="2" formControlName="description"></textarea>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Sponsor (optional)</mat-label>
        <mat-select formControlName="sponsorId">
          <mat-option value="">None</mat-option>
          <mat-option *ngFor="let s of sponsors" [value]="s.id">{{ s.name }} ({{ s.tier || 'partner' }})</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Placement from</mat-label>
        <input matInput type="number" formControlName="placementFrom" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>How many placements</mat-label>
        <input matInput type="number" formControlName="placementCount" />
      </mat-form-field>
      <app-image-url-field
        class="full"
        [challengeId]="id"
        controlName="photoUrl"
        label="Photo / icon"
        placeholderType="prize"
        [displayUrl]="editingDisplayUrl"
      ></app-image-url-field>
      <mat-form-field appearance="outline">
        <mat-label>Sort order</mat-label>
        <input matInput type="number" formControlName="sortOrder" />
      </mat-form-field>
      <div class="actions">
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || busy">Save</button>
        <button mat-button type="button" (click)="reset()" [disabled]="busy">Clear</button>
      </div>
    </form>
  </mat-card>

  <mat-card class="block">
    <h2>Winner prizes</h2>
    <div class="list" *ngIf="prizes.length; else empty">
      <div class="row" *ngFor="let p of prizes">
        <div class="row-main">
          <img class="thumb" [src]="prizeImageSrc(p)" [alt]="p.name" />
          <div>
          <strong>{{ p.name }}</strong>
          <div class="meta">
            rank {{ p.placementFrom }} · {{ p.placementCount }} placement(s)
            <span *ngIf="p.prizeValue"> · {{ p.prizeValue }}</span>
            <span *ngIf="p.sponsorId"> · {{ sponsorName(p.sponsorId) }}</span>
          </div>
          </div>
        </div>
        <div class="actions">
          <button mat-button type="button" (click)="edit(p)">Edit</button>
          <button mat-button type="button" (click)="remove(p.id)">Delete</button>
          <button
            mat-button
            type="button"
            *ngIf="canOverrideWinners"
            (click)="beginOverrideForPrize(p)"
          >
            Assign winner
          </button>
        </div>
      </div>
    </div>
    <ng-template #empty><p class="empty">No prize tiers yet.</p></ng-template>

    <h2 style="margin-top:1.25rem">Winners</h2>
    <div class="list" *ngIf="winners.length; else noWinners">
      <div class="row" *ngFor="let w of winners">
        <div>
          <strong>#{{ w.rank }} · {{ w.prizeName }}</strong>
          <div class="meta">
            {{ winnerLabel(w) }}
            <span *ngIf="w.prizeValue"> · {{ w.prizeValue }}</span>
            <span *ngIf="w.autoAssigned === false"> · overridden</span>
          </div>
        </div>
        <div class="actions" *ngIf="canOverrideWinners">
          <button mat-button type="button" (click)="beginOverride(w)">Change</button>
          <button mat-button type="button" (click)="clearWinner(w)">Clear</button>
        </div>
      </div>
    </div>
    <ng-template #noWinners><p class="empty">Winners appear after judging is finalized.</p></ng-template>

    <div *ngIf="canOverrideWinners" style="margin-top:1.25rem">
      <h2>Override winner</h2>
      <p class="meta" style="margin-bottom:0.75rem">
        Assign or replace a winner for a prize tier from finalized ranked submissions.
      </p>
      <form class="form-grid" [formGroup]="overrideForm" (ngSubmit)="applyOverride()">
        <mat-form-field appearance="outline">
          <mat-label>Prize tier</mat-label>
          <mat-select formControlName="prizeTierId">
            <mat-option *ngFor="let p of prizes" [value]="p.id">{{ p.name }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Rank slot</mat-label>
          <input matInput type="number" formControlName="rank" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label>Ranked submission</mat-label>
          <mat-select formControlName="candidateKey">
            <mat-option
              *ngFor="let s of rankedSubmissions; let i = index"
              [value]="s.teamId ? 'team:' + s.teamId : 'user:' + s.userId"
            >
              {{ candidateLabel(s, i) }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <div class="actions">
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="overrideForm.invalid || busy || !rankedSubmissions.length"
          >
            Save override
          </button>
        </div>
      </form>
      <p class="empty" *ngIf="!rankedSubmissions.length">No scored submissions available to assign.</p>
    </div>
  </mat-card>
</div>